import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { JSONSchema7 } from "json-schema";
import * as JsonRefs from "json-refs";
import jp from "jsonpath";
import { reportSchemaQuery } from "../api/reportQueries";
import { filterSchemaByData } from "@/features/reports/utils/filterSchemaByData";
import { eforceSchemaMapping } from "@/features/reports/config/eforceSchemaMapping";
import { genericSchemaMapping } from "@/features/reports/config/genericSchemaMapping";
import type { FieldMapping } from "@/features/reports/config/fieldMapping";
import type { EforceIncidentModel } from "../../../shared/interfaces/EforceModels";

export interface ResolveReportSchemaOptions {
  /** "local" resolves only local $refs (preview parity); "all" resolves
   *  everything (editor parity — json-refs' default filter). */
  refFilter?: "local" | "all";
  /** KAR-only: slice the schema to the sections present in the incident. */
  karSlice?: boolean;
  /** Rewrite every `oneOf` into an `enum` of its const values (the editor's
   *  long-standing transform — RJSF renders selects from enums). */
  oneOfToEnum?: boolean;
  rms: string | null;
  incident: EforceIncidentModel | null;
}

/** Pure transform: every node carrying `oneOf` gains `enum` of the consts.
 *  Mutates (and returns) the given schema — pass a freshly resolved clone. */
export function enumifyOneOf(schema: JSONSchema7): JSONSchema7 {
  jp.query(schema, "$..[?(@.oneOf)]").forEach(
    (property: { oneOf: Array<{ const: unknown }>; enum?: unknown[] }) => {
      property.enum = property.oneOf.map((option) => option.const);
    },
  );
  return schema;
}

export function schemaMappingForRms(
  rms: string,
): Record<string, FieldMapping> {
  return rms === "EF" ? eforceSchemaMapping : genericSchemaMapping;
}

/** Pure async pipeline shared by every consumer: $ref resolution → optional
 *  KAR slice → optional oneOf→enum. Exported for unit tests. */
export async function resolveReportSchema(
  raw: JSONSchema7,
  opts: ResolveReportSchemaOptions,
): Promise<{ jsonSchema: JSONSchema7; schemaMapping: Record<string, FieldMapping> }> {
  const result = await JsonRefs.resolveRefs(
    raw,
    opts.refFilter === "all" ? undefined : { filter: "local" },
  );
  let resolved = result.resolved as JSONSchema7;
  if (opts.karSlice && opts.incident && opts.rms === "KAR") {
    resolved = filterSchemaByData(opts.incident, resolved);
  }
  if (opts.oneOfToEnum) {
    resolved = enumifyOneOf(resolved);
  }
  return {
    jsonSchema: resolved,
    schemaMapping: schemaMappingForRms(opts.rms ?? ""),
  };
}

export interface UseResolvedReportSchemaOptions
  extends Omit<ResolveReportSchemaOptions, "rms" | "incident"> {
  enabled?: boolean;
  /** reportType-specific schema (editor); omit for the station default. */
  reportType?: string;
  rms: string | null;
  incident: EforceIncidentModel | null;
}

/**
 * The single $ref-resolution pipeline over the cached raw schema
 * (reportSchemaQuery, staleTime Infinity). Replaces the copies that lived
 * in ExtractedInfoPage and useReportData; CoReviewPage still runs its own
 * inline pipeline (migrating it is Phase 4).
 *
 * Resolution is keyed on dataUpdatedAt, not the data reference: structural
 * sharing returns the SAME object for a deep-equal refetch, so retry()
 * must observe the refetch through the timestamp (the Phase 2 fix).
 *
 * KAR slicing waits for the incident (the old Promise.allSettled ordering):
 * resolving against incident=null would commit an unsliced schema that the
 * `jsonSchema` latch then never re-slices. When the incident fetch fails,
 * resolution simply stays pending — callers surface the incident error
 * themselves, and a successful retry releases the gate.
 */
export function useResolvedReportSchema(opts: UseResolvedReportSchemaOptions) {
  const {
    enabled = true,
    reportType,
    rms,
    incident,
    karSlice = false,
    oneOfToEnum = false,
    refFilter = "local",
  } = opts;

  const [jsonSchema, setJsonSchema] = useState<JSONSchema7 | null>(null);
  const [schemaMapping, setSchemaMapping] = useState<Record<
    string,
    FieldMapping
  > | null>(null);
  const [resolveError, setResolveError] = useState<unknown>(null);
  // The reportType the latched resolution was produced FOR. A mismatch (the
  // editor's reportType can change mid-session via the WS push or an
  // incident refetch) releases the latch stale-while-revalidate: the old
  // schema keeps rendering until the new type's resolution lands — nulling
  // it would feed RJSF a null schema, since consumers' loading latches
  // don't re-arm.
  const [resolvedFor, setResolvedFor] = useState<string | undefined | null>(
    null,
  );
  const isStale = !!jsonSchema && resolvedFor !== (reportType ?? undefined);

  const schemaQuery = useQuery({
    ...reportSchemaQuery(reportType),
    enabled: enabled && !!rms && (isStale || (!jsonSchema && !schemaMapping)),
  });

  const incidentMissing = karSlice && rms === "KAR" && !incident;
  const rawSchema = schemaQuery.data;
  const schemaUpdatedAt = schemaQuery.dataUpdatedAt;
  useEffect(() => {
    if (!enabled || !rawSchema || !rms) return;
    if (!isStale && (jsonSchema || schemaMapping)) return;
    if (incidentMissing) return;
    let cancelled = false;
    resolveReportSchema(rawSchema, {
      refFilter,
      karSlice,
      oneOfToEnum,
      rms,
      incident,
    })
      .then((resolved) => {
        if (cancelled) return;
        setJsonSchema(resolved.jsonSchema);
        setSchemaMapping(resolved.schemaMapping);
        setResolvedFor(reportType ?? undefined);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to load report schema:", error);
        setResolveError(error);
      });
    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    rawSchema,
    schemaUpdatedAt,
    rms,
    incident,
    incidentMissing,
    jsonSchema,
    schemaMapping,
    isStale,
    reportType,
    refFilter,
    karSlice,
    oneOfToEnum,
  ]);

  const fetchError = schemaQuery.error;
  return {
    jsonSchema,
    schemaMapping,
    isError: !!fetchError || !!resolveError,
    /** The fetch error when the GET failed, else the $ref-resolution error. */
    error: fetchError ?? resolveError,
    /** Re-runs fetch + resolution; dataUpdatedAt advances even when the
     *  refetched schema is deep-equal, so resolution always re-runs. */
    retry: () => {
      setResolveError(null);
      void schemaQuery.refetch();
    },
  };
}
